import React, { useEffect, useState } from "react";
import { DashboardShell } from "@/components/DashboardLayout";
import { BackButton } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { VoucherCard } from "@/components/VoucherCard";
import { api, fmtSASTDate, fmtZAR, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Copy, Check, FileDown, FileText } from "lucide-react";
import { downloadCsv, printVoucherBatch } from "@/lib/exporters";

export default function MerchantVouchers() {
  const { merchant, refresh } = useAuth();
  const [providers, setProviders] = useState([]);
  const [vouchers, setVouchers] = useState([]);
  const [form, setForm] = useState({ amount: "200", quantity: "1", intended_service: "general", recipient_phone: "", notes: "" });
  const [issued, setIssued] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState({});

  const load = async () => {
    try {
      const { data } = await api.get("/vouchers");
      setVouchers(data.vouchers || []);
    } catch {}
  };

  useEffect(() => {
    api.get("/providers").then(({ data }) => setProviders(data.providers || []));
    load();
  }, []);

  const generate = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/vouchers/generate", {
        amount: parseFloat(form.amount),
        quantity: parseInt(form.quantity, 10),
        intended_service: form.intended_service,
        recipient_phone: form.recipient_phone || null,
        notes: form.notes,
      });
      setIssued(data);
      toast.success(`Issued ${data.issued.length} voucher(s)`);
      await load();
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const copyPin = async (pin, id) => {
    try {
      await navigator.clipboard.writeText(pin);
      setCopied({ ...copied, [id]: true });
      setTimeout(() => setCopied((c) => ({ ...c, [id]: false })), 1500);
    } catch {}
  };

  const exportCsvAll = () => {
    if (vouchers.length === 0) {
      toast.info("No vouchers to export");
      return;
    }
    const rows = vouchers.map((v) => ({
      id: v.id,
      pin_masked: v.pin_masked,
      original_amount_zar: Number(v.original_amount || 0).toFixed(2),
      balance_zar: Number(v.balance || 0).toFixed(2),
      status: v.status,
      service: v.intended_service || "general",
      recipient_phone: v.recipient_phone || "",
      created_at: v.created_at,
      expires_at: v.expires_at,
    }));
    downloadCsv(`flexivouch-vouchers-${new Date().toISOString().slice(0, 10)}.csv`, rows);
    toast.success(`Exported ${rows.length} vouchers to CSV`);
  };

  const exportPdfAll = () => {
    if (vouchers.length === 0) {
      toast.info("No vouchers to export");
      return;
    }
    printVoucherBatch("All Vouchers", vouchers, {
      showFullPin: false,
      merchantName,
    });
  };

  const exportIssuedCsv = () => {
    if (!issued?.issued?.length) return;
    const rows = issued.issued.map((v) => ({
      id: v.id,
      pin: v.pin,
      amount_zar: Number(v.amount || 0).toFixed(2),
      expires_at: v.expires_at,
    }));
    downloadCsv(`flexivouch-batch-${new Date().toISOString().slice(0, 19).replace(/[:.]/g, "-")}.csv`, rows);
    toast.success(`Exported ${rows.length} PINs to CSV`);
  };

  const exportIssuedPdf = () => {
    if (!issued?.issued?.length) return;
    printVoucherBatch(`Freshly issued batch · ${issued.issued.length} voucher${issued.issued.length === 1 ? "" : "s"}`, issued.issued, {
      showFullPin: true,
      merchantName,
    });
  };

  return (
    <DashboardShell role="merchant">
      <BackButton to="/dashboard" />
      <p className="text-xs tracking-[0.22em] uppercase font-bold text-[#00C4B4]">Voucher engine</p>
      <h1 className="font-display mt-2 text-3xl font-bold text-[#0A2540]">Issue & manage vouchers</h1>

      <form onSubmit={generate} data-testid="voucher-generate-form" className="mt-6 bg-white border border-[#E5E7EB] rounded-2xl p-6 grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="space-y-2"><Label htmlFor="va">Amount (ZAR)</Label><Input data-testid="voucher-amount-input" id="va" type="number" min="1" step="0.01" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></div>
        <div className="space-y-2"><Label htmlFor="vq">Quantity</Label><Input data-testid="voucher-qty-input" id="vq" type="number" min="1" max="100" required value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} /></div>
        <div className="space-y-2">
          <Label>Service</Label>
          <Select value={form.intended_service} onValueChange={(v) => setForm({ ...form, intended_service: v })}>
            <SelectTrigger data-testid="voucher-service-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {providers.map((p) => <SelectItem key={p.name} value={p.name}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2"><Label htmlFor="vp">Recipient phone (optional)</Label><Input data-testid="voucher-phone-input" id="vp" value={form.recipient_phone} onChange={(e) => setForm({ ...form, recipient_phone: e.target.value })} placeholder="082 123 4567" /></div>
        <div className="space-y-2 flex flex-col"><Label className="opacity-0">_</Label>
          <Button data-testid="voucher-generate-btn" type="submit" disabled={busy || merchant?.kyc_status !== "approved"} className="bg-[#00C4B4] hover:bg-[#009E91] text-white">
            {busy ? "Issuing…" : "Generate"}
          </Button>
        </div>
      </form>

      <div className="mt-10 flex items-end justify-between flex-wrap gap-3">
        <h2 className="font-display text-xl font-semibold text-[#0A2540]">Your vouchers ({vouchers.length})</h2>
        <div className="flex gap-2">
          <Button
            data-testid="export-csv-btn"
            onClick={exportCsvAll}
            disabled={vouchers.length === 0}
            variant="outline"
            className="border-[#0A2540] text-[#0A2540] hover:bg-[#0A2540] hover:text-white rounded-full"
          >
            <FileDown size={16} className="mr-2" /> CSV
          </Button>
          <Button
            data-testid="export-pdf-btn"
            onClick={exportPdfAll}
            disabled={vouchers.length === 0}
            variant="outline"
            className="border-[#00C4B4] text-[#00C4B4] hover:bg-[#00C4B4] hover:text-white rounded-full"
          >
            <FileText size={16} className="mr-2" /> PDF
          </Button>
        </div>
      </div>
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="vouchers-grid">
        {vouchers.length === 0 && <p className="text-sm text-[#666666] col-span-full">No vouchers yet — generate your first above.</p>}
        {vouchers.map((v) => <VoucherCard key={v.id} voucher={v} />)}
      </div>

      <Dialog open={!!issued} onOpenChange={(o) => { if (!o) setIssued(null); }}>
        <DialogContent className="max-w-lg" data-testid="issued-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[#0A2540]">Vouchers issued — save the PINs!</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#666666]">PINs are shown only once. Copy and store securely.</p>
          <div className="mt-4 space-y-3 max-h-72 overflow-y-auto">
            {issued?.issued?.map((v) => (
              <div key={v.id} className="border border-[#E5E7EB] rounded-lg p-4 bg-[#F8F9FA]" data-testid={`issued-voucher-${v.id}`}>
                <div className="flex items-center justify-between">
                  <span className="font-mono text-lg text-[#0A2540] tracking-wider">{v.pin}</span>
                  <button onClick={() => copyPin(v.pin, v.id)} data-testid={`copy-pin-${v.id}`} className="flex items-center gap-1 text-xs font-semibold text-[#00C4B4] hover:text-[#009E91]">
                    {copied[v.id] ? <Check size={14} /> : <Copy size={14} />}
                    {copied[v.id] ? "Copied" : "Copy"}
                  </button>
                </div>
                <p className="text-xs text-[#666666] mt-1">Value {fmtZAR(v.amount)} · Expires {fmtSASTDate(v.expires_at)}</p>
              </div>
            ))}
          </div>
          <p className="text-xs text-[#0A2540] mt-3">Wallet balance: <strong>{fmtZAR(issued?.wallet_balance || 0)}</strong></p>
          <div className="mt-4 flex gap-2 pt-3 border-t border-[#E5E7EB]">
            <Button
              data-testid="issued-export-csv-btn"
              onClick={exportIssuedCsv}
              variant="outline"
              className="flex-1 border-[#0A2540] text-[#0A2540] hover:bg-[#0A2540] hover:text-white"
            >
              <FileDown size={16} className="mr-2" /> Download CSV (full PINs)
            </Button>
            <Button
              data-testid="issued-export-pdf-btn"
              onClick={exportIssuedPdf}
              className="flex-1 bg-[#00C4B4] hover:bg-[#009E91] text-white"
            >
              <FileText size={16} className="mr-2" /> Print batch sheet
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardShell>
  );
}
